import { defineStore } from 'pinia';
import InternalChatAPI from 'dashboard/api/internalChat';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';

export const useInternalChatStore = defineStore('internalChat', {
  state: () => ({
    threads: [],
    messagesByThread: {}, // { threadId: [messages asc] }
    activeThreadId: null,
    loading: false,
  }),
  getters: {
    sortedThreads: state =>
      [...state.threads].sort((a, b) => b.last_activity_at - a.last_activity_at),
    totalUnread: state =>
      state.threads.reduce((sum, t) => sum + (t.unread_count || 0), 0),
  },
  actions: {
    async fetchThreads() {
      this.loading = true;
      try {
        const { data } = await InternalChatAPI.getThreads();
        this.threads = data.threads;
      } finally {
        this.loading = false;
      }
    },
    async openDirect(userId) {
      const { data } = await InternalChatAPI.createDirect(userId);
      this.upsertThread(data);
      await this.selectThread(data.id);
      return data;
    },
    async openTeam(teamId) {
      const { data } = await InternalChatAPI.createTeam(teamId);
      this.upsertThread(data);
      await this.selectThread(data.id);
      return data;
    },
    async selectThread(threadId) {
      this.activeThreadId = threadId;
      await this.fetchMessages(threadId);
      await this.markRead(threadId);
    },
    async fetchMessages(threadId, before = null) {
      const { data } = await InternalChatAPI.getMessages(threadId, before);
      const asc = [...data.messages].reverse();
      this.messagesByThread[threadId] = before
        ? [...asc, ...(this.messagesByThread[threadId] || [])]
        : asc;
    },
    async sendMessage(threadId, content) {
      const { data } = await InternalChatAPI.sendMessage(threadId, content);
      this.appendMessage(data); // eco dedup por id
    },
    async markRead(threadId) {
      await InternalChatAPI.markRead(threadId);
      const t = this.threads.find(x => x.id === threadId);
      if (t) t.unread_count = 0;
    },
    // ---- realtime handlers (chamados via actionCable.js) ----
    onMessageCreated(data) {
      this.appendMessage(data);
      const t = this.threads.find(x => x.id === data.internal_thread_id);
      // Thread desconhecida (ex.: DM iniciada pelo outro lado): refaz a lista
      // para que a nova thread apareca sem refresh manual.
      if (!t) {
        this.fetchThreads();
        return;
      }
      t.last_activity_at = data.created_at;
      if (data.internal_thread_id !== this.activeThreadId) {
        t.unread_count = data.unread_count;
        DashboardAudioNotificationHelper.playAudioAlert?.();
      } else {
        this.markRead(data.internal_thread_id);
      }
    },
    onThreadRead(data) {
      const t = this.threads.find(x => x.id === data.internal_thread_id);
      if (t) t.unread_count = data.unread_count;
    },
    // ---- helpers ----
    appendMessage(message) {
      const list = this.messagesByThread[message.internal_thread_id] || [];
      if (list.some(m => m.id === message.id)) return; // dedup eco
      this.messagesByThread[message.internal_thread_id] = [...list, message];
    },
    upsertThread(thread) {
      const idx = this.threads.findIndex(t => t.id === thread.id);
      if (idx >= 0) this.threads.splice(idx, 1, thread);
      else this.threads.push(thread);
    },
  },
});
